import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { MButton, MSelect, MTextArea } from '@Components/Base';
import { Flex, Modal, Typography } from 'antd';
import { FormikProps } from 'formik';
import { useGetTenderApproversQuery } from '@Features/MaterialRequest/requests/TenderRequest/store/api/TenderRequestApiSlice';
import { DefaultOptionType } from 'antd/es/select';
import { initFormikValuesType } from '../../../types';

import styles from './approve-modal.module.css';
import { transformResponseToOptionGroups } from '../utils';

interface ApproveModalDesktopProps {
  isModalOpen: boolean;
  formik: FormikProps<initFormikValuesType>;
  handleApprove: () => void;
  handleReject: () => void;
  handleCancel: () => void;
  hideApproverSelection: boolean;
}

const ApproveModalDesktop: React.FC<ApproveModalDesktopProps> = ({
  formik,
  handleCancel,
  handleApprove,
  handleReject,
  isModalOpen,
  hideApproverSelection,
}) => {
  const { tenderId } = useParams();
  const { data } = useGetTenderApproversQuery(Number(tenderId));
  const [approversOptions, setApproversOptions] = useState<
    Array<DefaultOptionType>
  >([]);

  useEffect(() => {
    if (data) {
      const optionGroups = transformResponseToOptionGroups(data);
      setApproversOptions(optionGroups || []);
    }
  }, [data]);

  const customButtons = useMemo(() => {
    return [
      <MButton key="back" id="cancel" type="tertiary" onClick={handleCancel}>
        Cancel
      </MButton>,
      <MButton key="reject" danger onClick={handleReject}>
        Reject
      </MButton>,
      <MButton key="submit" id="ok" type="primary" onClick={handleApprove}>
        Approve
      </MButton>,
    ];
  }, [handleApprove, handleCancel, handleReject]);

  const showApproverSelector = useMemo(() => {
    return (
      !hideApproverSelection &&
      Boolean(
        data?.approvers || data?.commercialApprovers || data?.technicalApprovers
      )
    );
  }, [
    data?.approvers,
    data?.commercialApprovers,
    data?.technicalApprovers,
    hideApproverSelection,
  ]);

  return (
    <Modal
      open={isModalOpen}
      onOk={handleApprove}
      onCancel={handleCancel}
      className={styles.modal}
      footer={customButtons}
      closeIcon={false}
      maskClosable={false}
    >
      <Flex vertical>
        <Flex className={styles.titleSection}>
          <Typography.Text className={styles.title}>
            Approval Confirmation
          </Typography.Text>
        </Flex>
        <div className={styles.fieldsSection}>
          {showApproverSelector && (
            <div>
              <Typography.Text>
                Please choose the respective Approver:
              </Typography.Text>
              <MSelect
                style={{ width: '100%' }}
                options={approversOptions}
                onChange={(value) => {
                  formik.setFieldValue('approver', String(value));
                }}
                placeholder="Choose Approver"
              />
            </div>
          )}
          <div>
            <Typography.Text>Comments:</Typography.Text>
            <MTextArea
              name="comments"
              onChange={(input) =>
                formik.setFieldValue('comment', input.target.value)
              }
              rows={4}
              showCount
              maxLength={100}
              placeholder="Insert comment"
            />
          </div>
        </div>
      </Flex>
    </Modal>
  );
};

export default ApproveModalDesktop;
